/**
 * Event grouping for the review grid.
 *
 * Cards arrive in backend order (frames of the same event are emitted
 * consecutively). We don't reorder them — we just detect *runs* of
 * consecutive cards that share an `event_uuid` and give each run a colour by
 * its position in the sequence, so the reviewer can see at a glance which
 * tiles belong to the same event.
 *
 * Pure + deterministic so it's unit-testable.
 */

/**
 * Colour for the Nth event group (0-based) in display order. Uses golden-angle
 * hue rotation (~137.5°) so CONSECUTIVE groups land far apart on the colour
 * wheel — adjacent events never share or get near the same border colour, so
 * two events back-to-back don't blur into one big block. Mid lightness/strong
 * saturation keeps the dashed border legible on both light and dark themes.
 */
export const eventGroupColor = (index: number): string => {
  const hue = Math.round((index * 137.508) % 360);
  return `hsl(${hue}, 70%, 50%)`;
};

export type EventGroupPosition = "single" | "start" | "middle" | "end";

export interface EventGroupInfo {
  /**
   * Colour for this card's event group (assigned by the group's position in
   * the sequence), or `null` when the card is not part of a multi-card run
   * (isolated single-image events get no box).
   */
  color: string | null;
  /** Size of the consecutive run this card belongs to. */
  runSize: number;
  /** Where this card sits within its run. */
  position: EventGroupPosition;
}

/**
 * Walk the items once, grouping maximal runs of consecutive identical
 * `event_uuid`. Returns one EventGroupInfo per item (same length / order).
 */
export const computeEventGroups = (
  items: ReadonlyArray<{ event_uuid: string }>,
): EventGroupInfo[] => {
  const result: EventGroupInfo[] = [];
  let i = 0;
  // Colour each multi-card group by its position in the sequence so adjacent
  // groups are always visually distinct. Single-card events get no colour and
  // don't consume an index.
  let groupIndex = 0;
  while (i < items.length) {
    const uuid = items[i].event_uuid;
    let j = i;
    while (j < items.length && items[j].event_uuid === uuid) j++;
    const runSize = j - i;
    const isGroup = runSize >= 2 && !!uuid;
    const color = isGroup ? eventGroupColor(groupIndex) : null;
    if (isGroup) groupIndex++;
    for (let k = i; k < j; k++) {
      if (!isGroup) {
        result.push({ color: null, runSize, position: "single" });
      } else {
        const position: EventGroupPosition =
          k === i ? "start" : k === j - 1 ? "end" : "middle";
        result.push({ color, runSize, position });
      }
    }
    i = j;
  }
  return result;
};

export interface EventRow<T> {
  /** The shared event_uuid for every item in this row. */
  uuid: string;
  /** The frames of this event, in backend order. */
  items: T[];
}

/**
 * Group items into one row per maximal run of consecutive identical
 * `event_uuid`. Same walk as `computeEventGroups`, but returns the items
 * bucketed by run instead of a per-item annotation. Pure + deterministic.
 */
export const groupIntoEventRows = <T extends { event_uuid: string }>(
  items: ReadonlyArray<T>,
): EventRow<T>[] => {
  const rows: EventRow<T>[] = [];
  let i = 0;
  while (i < items.length) {
    const uuid = items[i].event_uuid;
    let j = i;
    while (j < items.length && items[j].event_uuid === uuid) j++;
    rows.push({ uuid, items: items.slice(i, j) });
    i = j;
  }
  return rows;
};
